import Button from 'ext-ui/components/Button/Button'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeeds/CardFeeds.style'
import CardFeedsTable from 'src/app/CardFeeds/components/CardFeedsTable'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  cardFeedDetailsUrl,
  createCardFeedUrl,
  homeUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import useQueryCardFeeds from 'src/queries/cardFeeds/domain/useQueryCardFeeds'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { Userpilot } from 'userpilot'

const CardFeeds: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const user = useSelector(selectUserListModelFromState)
  const {
    data: cardFeeds,
    isLoading: isFeedsLoading,
    isError: isFeedsError,
    isFetchedAfterMount: isFeedsFetchedAfterMount,
  } = useQueryCardFeeds(user?.organizationId, isAllowed)

  // Defensive: sidebar gates the menu entry, but a direct URL must redirect
  // home when either flag is off. Wait for the feature query to settle so we
  // don't bounce away during the initial paint.
  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsCardFeedsTab)
    analytics.track('card feeds - viewed')
  }, [analytics, isAllowed])

  useEffect(() => {
    if (!isAllowed || !isFeedsError) return
    analytics.track('card feeds', { action: 'load failed' })
  }, [analytics, isAllowed, isFeedsError])

  // With no feeds, route the user straight into creation — but only once we've
  // confirmed *this mount* that the org is empty. isFetchedAfterMount gates out
  // stale cached data from a previous visit, which otherwise bounced users who
  // do have feeds straight to the create page.
  const hasNoFeeds =
    isAllowed &&
    isFeedsFetchedAfterMount &&
    !isFeedsError &&
    cardFeeds?.length === 0
  useEffect(() => {
    if (hasNoFeeds) {
      history.replace(createCardFeedUrl())
    }
  }, [hasNoFeeds, history])

  // Hold the gate with a full-page loader while the feature query resolves —
  // otherwise we flash a blank screen before either the page or the redirect.
  if (isFeaturesLoading) {
    return <LoadingBackgroundPage />
  }

  if (!isAllowed) {
    return null
  }

  const handleHelpGuideClick = (): void => {
    Userpilot.trigger('resource_center:jNfDAOvnub')
    analytics.track('card feeds', { action: 'help guide clicked' })
  }

  const handleAddCardFeedClick = (): void => {
    analytics.track('card feeds - add card feed')
    history.push(createCardFeedUrl())
  }

  const handleSeeEnrolledCardsClick = (feedId: string): void => {
    analytics.track('card feeds', { action: 'see enrolled cards clicked' })
    history.push(cardFeedDetailsUrl(feedId))
  }

  const actions = (
    <div css={style.actions}>
      <Button
        variant="secondary"
        onClick={handleHelpGuideClick}
        data-testid="card-feeds-help-guide"
        iconLeft={
          <SvgExternalLink
            viewBox="0 0 24 24"
            css={style.helpGuideIcon}
            data-testid="card-feeds-help-guide-icon"
          />
        }
      >
        {translate('Help Guide')}
      </Button>
      <Button
        variant="primary"
        onClick={handleAddCardFeedClick}
        data-testid="card-feeds-add"
      >
        {`+ ${translate('Add Card Feed')}`}
      </Button>
    </div>
  )

  const feeds = cardFeeds ?? []

  return (
    <>
      <Helmet>
        <title>{translate('Card Feeds')}</title>
      </Helmet>
      <LayoutPage
        title={<span>{translate('Card Feeds')}</span>}
        subtitle={
          <span css={style.subtitle}>
            {translate(
              'Connect your bank to bring existing cards into Extend, then issue virtual cards and manage spend in one place.',
            )}
          </span>
        }
        actions={actions}
      >
        <CardFeedsTable
          feeds={feeds}
          isLoading={isFeedsLoading}
          isError={isFeedsError}
          onSeeCards={handleSeeEnrolledCardsClick}
        />
      </LayoutPage>
    </>
  )
}

export default CardFeeds
